/**
 * Auto-applies MEMBERWELCOME when membership + other items are in cart.
 * Discount math is server-side (Shopify Function); this only applies the code.
 */

let welcomeApplyInFlight = false;
let welcomeAppliedForCartKey = '';

/**
 * Either membership product may be the line in the cart: carts predating the
 * 2026 product still hold the legacy one, and both grant membership.
 */
function getMembershipProductIds(body) {
  const raw = body.dataset.membershipProductIds || body.dataset.membershipProductId || '';
  return raw
    .split(',')
    .map((value) => Number(value.trim()))
    .filter((value) => value && !Number.isNaN(value));
}

function getMembershipWelcomeConfig() {
  const body = document.body;
  if (!body) return null;

  const membershipProductIds = getMembershipProductIds(body);
  const code = (body.dataset.membershipWelcomeCode || 'MEMBERWELCOME').trim();
  const isMember = body.dataset.isMember === 'true';

  if (!code || !membershipProductIds.length) return null;

  return { membershipProductIds, code, isMember };
}

async function fetchCartJson() {
  const response = await fetch(`/cart.js?timestamp=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) throw new Error('Unable to load cart');
  return response.json();
}

function membershipProductInCart(cart, membershipProductIds) {
  return cart.items.some((item) => membershipProductIds.includes(item.product_id));
}

function cartHasNonMembershipLines(cart, membershipProductIds) {
  return cart.items.some((item) => !membershipProductIds.includes(item.product_id));
}

function memberWelcomeApplied(cart, code, membershipProductIds) {
  const normalized = code.toUpperCase();
  const discountEntry = (cart.discount_codes || []).find(
    (entry) => entry.code && entry.code.toUpperCase() === normalized
  );
  const codeOnCart = Boolean(discountEntry && discountEntry.applicable);

  const hasLineDiscount = cart.items.some((item) => {
    if (membershipProductIds.includes(item.product_id)) return false;
    if (item.total_discount > 0) return true;
    return Array.isArray(item.line_level_discount_allocations)
      && item.line_level_discount_allocations.length > 0;
  });

  return codeOnCart && hasLineDiscount;
}

function getCartSyncKey(cart) {
  return cart.items
    .map((item) => `${item.key}:${item.quantity}:${item.final_line_price}`)
    .join('|');
}

async function applyWelcomeDiscount(code) {
  await fetch(`/discount/${encodeURIComponent(code)}`, {
    method: 'GET',
    redirect: 'manual'
  });
}

function refreshCartUi() {
  document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));

  const cartItems = document.getElementById('cart-items');
  if (cartItems && typeof cartItems.refresh === 'function') {
    cartItems.refresh();
  }
}

async function syncMembershipWelcome() {
  const config = getMembershipWelcomeConfig();
  if (!config || config.isMember || welcomeApplyInFlight) return false;

  const { membershipProductIds, code } = config;

  let cart;
  try {
    cart = await fetchCartJson();
  } catch (error) {
    console.error('Membership welcome cart fetch error:', error);
    return false;
  }

  const eligible =
    membershipProductInCart(cart, membershipProductIds)
    && cartHasNonMembershipLines(cart, membershipProductIds);

  if (!eligible) {
    welcomeAppliedForCartKey = '';
    return false;
  }

  if (memberWelcomeApplied(cart, code, membershipProductIds)) {
    return true;
  }

  const cartKey = getCartSyncKey(cart);
  if (welcomeAppliedForCartKey === cartKey) {
    return false;
  }

  welcomeApplyInFlight = true;

  try {
    await applyWelcomeDiscount(code);
    welcomeAppliedForCartKey = cartKey;

    let updatedCart;
    try {
      updatedCart = await fetchCartJson();
    } catch (error) {
      updatedCart = null;
    }

    if (updatedCart && memberWelcomeApplied(updatedCart, code, membershipProductIds)) {
      refreshCartUi();
      return true;
    }

    refreshCartUi();
    return false;
  } catch (error) {
    console.error('Membership welcome discount apply error:', error);
    return false;
  } finally {
    welcomeApplyInFlight = false;
  }
}

function bindMembershipWelcomeEvents() {
  if (document.documentElement.dataset.membershipWelcomeBound === 'true') return;
  document.documentElement.dataset.membershipWelcomeBound = 'true';

  const schedule = () => {
    window.clearTimeout(bindMembershipWelcomeEvents.timer);
    bindMembershipWelcomeEvents.timer = window.setTimeout(() => {
      syncMembershipWelcome();
    }, 150);
  };

  document.addEventListener('dispatch:cart-drawer:refresh', schedule);
  document.addEventListener('on:cart:add', schedule);
  document.addEventListener('on:line-item:change', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
}

bindMembershipWelcomeEvents();

window.syncMembershipWelcome = syncMembershipWelcome;
